import { Component, Input, signal, viewChild } from '@angular/core';
import { WidgetBase } from '../../../models';
import { MedicalNotesWidgetPlaceholder } from './medical-notes-widget-placeholder';
import { MedicalNotesWidgetPrint } from './medical-notes-widget-print';
import { MedicalNotesWidgetPreview } from './medical-notes-widget-preview';
import { MedicalNotesWidgetEdit } from './medical-notes-widget-edit';
import { MedicalNote } from './medical-note';
import { createLocalDate, parseLocalDateString } from '../../../helpers/date-utils';
import { MedicalNoteItem } from './medical-note-item';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';
import { copyScribeSuggestionMarker } from '../../scribe-suggestion-marker.util';

@Component({
  selector: 'fb-medical-notes-widget',
  imports: [
    MedicalNotesWidgetPlaceholder,
    MedicalNotesWidgetPrint,
    MedicalNotesWidgetPreview,
    MedicalNotesWidgetEdit,
  ],
  templateUrl: './medical-notes-widget.html',
  styleUrl: './medical-notes-widget.scss',
})
export class MedicalNotesWidget extends WidgetBase {
  @Input() public isTemplatePrint = false;
  /**
   * Normalized payload for medical notes widget.
   */
  protected readonly data = signal<readonly MedicalNoteItem[]>([]);

  /**
   * Appointment ID.
   */
  protected readonly appointmentIdValue = signal<string | undefined>(undefined);

  /**
   * Patient remote ID.
   */
  protected readonly patientRemoteIdValue = signal<string | undefined>(undefined);

  /**
   * Query for editable widget representation.
   */
  protected readonly edit = viewChild(MedicalNotesWidgetEdit);

  private appointmentValue: any;

  public override set appointment(value: any) {
    this.appointmentValue = value;

    this.setupAppointmentId();
  }
  public override get appointment() {
    return this.appointmentValue;
  }

  public ngOnInit(): void {
    this.normalizePayload();
    this.setupAppointmentId();
    this.setupPatientRemoteId();
    // Signal readiness for form designer to avoid hanging state.
    this.ready.next(true);
  }

  /**
   * Returns metadata to be included in the request.
   */
  public getMetadata() {
    const edit = this.edit();

    if (!edit) {
      return {};
    }

    return edit.metadata();
  }

  /**
   * Updates the payload.
   */
  public updatePayload(notes: readonly MedicalNoteItem[]): void {
    if (!this.payload['properties']) {
      return;
    }

    if (!this.payload.properties['items']) {
      this.payload.properties.items = { value: [] };
    }

    const currentItems = Array.isArray(this.payload.properties.items.value)
      ? this.payload.properties.items.value
      : [];

    this.payload.properties.items.value = notes.map((note, index) => {
      const result: ScribeSuggestionMarkerTarget = {
        id: note.id,
        date: parseLocalDateString(note.date),
        employee_id: note.employeeId,
        employee_name: note.employeeName,
        text: note.text,
      };
      const currentItem = currentItems[index];
      copyScribeSuggestionMarker(
        currentItem && typeof currentItem === 'object'
          ? (currentItem as ScribeSuggestionMarkerTarget)
          : (note as unknown as ScribeSuggestionMarkerTarget),
        result,
      );
      return result;
    });
  }

  /**
   * Updates both widget payload and normalized local state.
   */
  public setNotes(notes: readonly MedicalNoteItem[]): void {
    this.data.set(notes);
    this.updatePayload(notes);
  }

  protected getScribeMarkerTargets(): readonly (ScribeSuggestionMarkerTarget | null)[] {
    const items = this.payload?.properties?.items?.value;
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map((item) =>
      item && typeof item === 'object' && !Array.isArray(item)
        ? (item as ScribeSuggestionMarkerTarget)
        : null,
    );
  }

  /**
   * Normalizes the payload for medical notes widget.
   */
  private normalizePayload(): void {
    this.data.set(this.getData());
  }

  /**
   * Sets up the appointment ID.
   */
  private setupAppointmentId(): void {
    const appointmentId =
      this.appointment?.AppointmentId ??
      this.entity?.metadata?.appointment_id ??
      this.appointmentId;

    this.appointmentIdValue.set(appointmentId);
  }

  /**
   * Sets up the patient remote ID.
   */
  private setupPatientRemoteId(): void {
    this.patientRemoteIdValue.set(this.patient?.remote_id);
  }

  /**
   * Returns normalized medical notes from the widget payload.
   */
  private getData(): readonly MedicalNoteItem[] {
    const items = this.payload?.properties?.items?.value;

    if (!Array.isArray(items)) {
      return [];
    }

    const notes = items
      .map((item) => this.mapPayloadItemToNote(item))
      .filter((note): note is MedicalNote => note !== null);

    return this.mergeNotesWithMetadata(notes);
  }

  /**
   * Fills missing note IDs from metadata without dropping UI-only fields.
   */
  private mergeNotesWithMetadata(notes: readonly MedicalNoteItem[]): readonly MedicalNoteItem[] {
    if (notes.length === 0) {
      return notes;
    }

    const metadataNotes = this.getMetadataNotes();

    if (metadataNotes.length === 0) {
      return notes;
    }

    const metadataIndex = new Map<string, string>();

    metadataNotes.forEach((note) => {
      if (!note.id) {
        return;
      }

      const key = this.getNoteKey(note.date, note.employeeId, note.text);

      if (!key) {
        return;
      }

      metadataIndex.set(key, note.id);
    });

    if (metadataIndex.size === 0) {
      return notes;
    }

    return notes.map((note) => {
      if (note.id) {
        return note;
      }

      const key = this.getNoteKey(note.date, note.employeeId, note.text);
      const id = key ? metadataIndex.get(key) : undefined;

      return id ? { ...note, id } : note;
    });
  }

  /**
   * Normalizes metadata medical notes to a consistent shape for matching.
   */
  private getMetadataNotes() {
    const value = this.entity?.metadata?.medical_notes;

    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }

        const record = item as Record<string, unknown>;
        const rawId = record['id'];
        const dateValue = record['date'];
        const employeeValue = record['employee_id'];
        const textValue = record['medical_notes'];

        const date = this.coerceDate(dateValue);
        const employeeId =
          employeeValue !== undefined && employeeValue !== null ? String(employeeValue) : '';
        const text = textValue !== undefined && textValue !== null ? String(textValue) : '';
        const id = typeof rawId === 'string' || typeof rawId === 'number' ? String(rawId) : '';

        if (!date || !employeeId || !text) {
          return null;
        }

        return {
          id: id.length ? id : undefined,
          date,
          employeeId,
          text,
        };
      })
      .filter((note) => note !== null);
  }

  /**
   * Maps a payload item to a medical note.
   */
  private mapPayloadItemToNote(item: unknown): MedicalNoteItem | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const record = item as Record<string, unknown>;

    const extractIdProperty = (value: Record<string, unknown>): string | undefined => {
      const id = value['id'];

      if (typeof id === 'string') {
        return id;
      }

      return undefined;
    };

    const extractEmployeeNameProperty = (value: Record<string, unknown>): string | undefined => {
      const name = value['employee_name'];

      if (typeof name === 'string') {
        return name;
      }

      return undefined;
    };

    const id = extractIdProperty(record);
    const employeeName = extractEmployeeNameProperty(record);
    const dateValue = record['date'];
    const employeeValue = record['employee_id'] ?? record['employee'];
    const textValue = record['text'];

    const date = this.coerceDate(dateValue);
    const employeeId =
      employeeValue !== undefined && employeeValue !== null ? String(employeeValue).trim() : '';
    const text = textValue !== undefined && textValue !== null ? String(textValue).trim() : '';

    if (!date || !employeeId || !text) {
      return null;
    }

    const result: MedicalNoteItem = {
      id,
      date,
      employeeId,
      employeeName,
      text,
    };
    copyScribeSuggestionMarker(record, result as unknown as ScribeSuggestionMarkerTarget);
    return result;
  }

  /**
   * Coerces a value into a Date object.
   */
  private coerceDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date && !isNaN(value.getTime())) {
      return value;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const localDate = createLocalDate(value);

    if (localDate) {
      return localDate;
    }

    const parsed = new Date(value);

    return isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * Builds a stable key used for metadata matching.
   */
  private getNoteKey(dateValue: Date, employeeId: string, text: string): string | null {
    const dateKey = parseLocalDateString(dateValue);
    const normalizedEmployeeId = String(employeeId ?? '').trim();
    const normalizedText = String(text ?? '').trim();

    if (!dateKey || !normalizedEmployeeId || !normalizedText) {
      return null;
    }

    return `${dateKey}|${normalizedEmployeeId}|${normalizedText}`;
  }
}
